/**
 * Пункт навігації: іконка 20 px і підпис. Активний — `aria-current="page"` і смужка акценту
 * (UX-02). Довгий підпис обрізається (рейка — два рядки, розгорнута — один), повний — у підказці.
 *
 * Намір (наведення понад 50 мс, фокус, дотик) вантажить КОД розділу заздалегідь (UX-33,
 * app/preload.ts), без даних. Легкий режим не вантажить (lite-mode.md).
 */
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useIntentPreload } from '../app/preload.ts';
import { Icon } from '../ui/Icon.tsx';
import { isCurrentNav, type NavEntry } from './nav-model.ts';

export function NavLink({
  item,
  className,
  badge,
}: {
  item: NavEntry;
  className?: string;
  /** Лічильник поруч з підписом («Задачі N», tasks-today.md, розд. 3). */
  badge?: ReactNode;
}) {
  const intent = useIntentPreload(item.to);
  // Пункт сутності активний і на іншому її поданні чи картці; параметри запиту (?theme=, фільтри
  // подання) не роблять пункт неактивним: розділ той самий (UX-02, `isCurrentNav`).
  const current = useRouterState({ select: (s) => isCurrentNav(item, s.location.pathname) });
  return (
    <Link
      to={item.to}
      data-testid={`nav-${item.id}`}
      className={className}
      title={item.label}
      aria-current={current ? 'page' : undefined}
      // Власна активність Link — лише точна адреса (підмножина `isCurrentNav`): інакше TanStack
      // за префіксом шляху ставив би `aria-current` пункту з `exact` поверх нашого (рев'ю #902).
      activeOptions={{ exact: true, includeSearch: false }}
      {...intent}
    >
      <Icon name={item.icon} size={20} />
      <span className="nav__label">{item.label}</span>
      {badge}
    </Link>
  );
}
